import { HttpClient } from '@angular/common/http';
import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Client } from '../../events/models/client.model';
import { ApiResponse } from '../../models/api.model';
import { PrivateSellingWhitelistClient } from '../modals/manage-private-selling/manage-private-selling.component';
import { PrivateSellingWhitelistItem } from '../models/private-selling.model';

export interface WhitelistData {
  patronId: number;
  tagId: string;
}

@Injectable({
  providedIn: 'root',
})
export class PrivateSellingService {
  private http = inject(HttpClient);

  public privateSellingToggleEvent = new EventEmitter<boolean>();
  public initialWhitelist = signal<PrivateSellingWhitelistClient[]>([]);

  private clientApiRoot = '/bo/clients/private-selling' as const;
  private privateSellingApiRoot = '/bo/private-selling/patron-ids' as const;

  public checkForPatronIdChanges(
    whitelist: PrivateSellingWhitelistClient[]
  ): boolean {
    let haveDifferentValues = false;

    if (!whitelist.length) {
      return haveDifferentValues;
    }

    const initialWhitelist = this.initialWhitelist();
    if (whitelist.length !== initialWhitelist.length) {
      return true;
    }

    const newPatronIds = new Set(whitelist.map(a => a.patronId));
    const initialPatronIds = new Set(initialWhitelist.map(a => a.patronId));

    haveDifferentValues =
      [...newPatronIds].some(id => !initialPatronIds.has(id)) ||
      [...initialPatronIds].some(id => !newPatronIds.has(id));

    return haveDifferentValues;
  }

  public enablePrivateSelling(
    whitelistData: WhitelistData[],
    groupLinkId: number | null
  ): Observable<ApiResponse<PrivateSellingWhitelistItem[]>> {
    const url = groupLinkId
      ? `${this.clientApiRoot}/${groupLinkId}`
      : `${this.clientApiRoot}`;

    return this.http.patch<ApiResponse<PrivateSellingWhitelistItem[]>>(
      environment.apiURl + url,
      { whitelistData }
    );
  }

  public disablePrivateSelling(): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      environment.apiURl + `${this.clientApiRoot}`
    );
  }

  private clientCache$: Observable<ApiResponse<Client>> | null = null;

  public getClientWithWhitelist(
    groupLinkId: number | null,
    refresh: boolean = false
  ): Observable<ApiResponse<Client>> {
    const url = groupLinkId
      ? `${this.clientApiRoot}/${groupLinkId}`
      : `${this.clientApiRoot}`;

    if (groupLinkId === null) {
      if (refresh || !this.clientCache$) {
        this.clientCache$ = this.http
          .get<ApiResponse<Client>>(environment.apiURl + url)
          .pipe(shareReplay(1));
      }
      return this.clientCache$;
    }

    return this.http.get<ApiResponse<Client>>(environment.apiURl + url);
  }

  public updateWhitelist(
    whitelistData: WhitelistData[]
  ): Observable<ApiResponse<PrivateSellingWhitelistClient[]>> {
    return this.http.patch<ApiResponse<PrivateSellingWhitelistClient[]>>(
      environment.apiURl + `${this.privateSellingApiRoot}`,
      { whitelistData }
    );
  }

  public deleteWhitelist(): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      environment.apiURl + `${this.privateSellingApiRoot}`
    );
  }
}
